import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const empty = {
  name: "",
  employee_id: "",
  contact: "",
  email: "",
  address: "",
  notes: "",
};

export default function Buyers() {
  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [q, setQ] = useState("");
  const [empFilter, setEmpFilter] = useState("all");
  const navigate = useNavigate();

  const load = async () => {
    const [{ data: b }, { data: e }] = await Promise.all([
      api.get("/buyers"),
      api.get("/employees"),
    ]);
    setRows(b);
    setEmployees(e);
  };
  useEffect(() => {
    load();
  }, []);

  const empName = (id) => employees.find((e) => e.id === id)?.name || "—";

  const startNew = () => {
    setEditing(null);
    setForm({ ...empty, employee_id: empFilter !== "all" ? empFilter : "" });
    setOpen(true);
  };
  const startEdit = (r) => {
    setEditing(r);
    setForm({ ...empty, ...r });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.employee_id) return toast.error("Assign an employee");
    try {
      if (editing) {
        await api.put(`/buyers/${editing.id}`, form);
        toast.success("Buyer updated");
      } else {
        await api.post("/buyers", form);
        toast.success("Buyer added");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  const remove = async (r) => {
    if (!window.confirm(`Move ${r.name} to recycle bin?`)) return;
    await api.delete(`/buyers/${r.id}`);
    toast.success("Moved to recycle bin");
    load();
  };

  const filtered = rows
    .filter((r) => (empFilter === "all" ? true : r.employee_id === empFilter))
    .filter((r) => r.name.toLowerCase().includes(q.trim().toLowerCase()));

  return (
    <div className="p-8 lg:p-12 max-w-[1400px]">
      <div className="flex items-end justify-between mb-8 animate-fade-up">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-ink-muted">
            Directory
          </div>
          <h1 className="font-display text-5xl font-semibold tracking-tight mt-1">
            Buyers
          </h1>
          <p className="text-ink-muted mt-2">
            Grouped by the employee they belong to.
          </p>
        </div>
        <Button className="btn-primary py-5" onClick={startNew} data-testid="buyers-new-btn">
          <Plus size={16} strokeWidth={1.75} /> New Buyer
        </Button>
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search buyer…"
          className="hygenix-input max-w-xs"
          data-testid="buyers-search"
        />
        <Select value={empFilter} onValueChange={setEmpFilter}>
          <SelectTrigger className="hygenix-input w-52" data-testid="buyers-employee-filter">
            <SelectValue placeholder="All employees" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All employees</SelectItem>
            {employees.map((e) => (
              <SelectItem key={e.id} value={e.id}>
                {e.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="card-surface overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-bone-50 border-b border-neutral-200">
            <tr>
              <th className="text-left px-4 py-3 text-[11px] uppercase tracking-wider text-ink-muted">
                Name
              </th>
              <th className="text-left px-4 py-3 text-[11px] uppercase tracking-wider text-ink-muted">
                Employee
              </th>
              <th className="text-left px-4 py-3 text-[11px] uppercase tracking-wider text-ink-muted">
                Contact
              </th>
              <th className="text-left px-4 py-3 text-[11px] uppercase tracking-wider text-ink-muted">
                Email
              </th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan="5" className="text-center p-10 text-ink-muted">
                  {employees.length === 0
                    ? "Add at least one employee first."
                    : "No buyers yet."}
                </td>
              </tr>
            )}
            {filtered.map((r) => (
              <tr
                key={r.id}
                className="border-b border-neutral-100 last:border-0 hover:bg-bone-50 transition-colors cursor-pointer"
                data-testid={`buyer-row-${r.name}`}
                onClick={() => navigate(`/bills?buyer=${r.id}`)}
                title={`View ${r.name}'s transactions`}
              >
                <td className="px-4 py-3 font-medium">{r.name}</td>
                <td className="px-4 py-3">
                  <span className="chip bg-forest-100 text-forest-700">
                    {empName(r.employee_id)}
                  </span>
                </td>
                <td className="px-4 py-3 text-ink-muted">{r.contact}</td>
                <td className="px-4 py-3 text-ink-muted">{r.email}</td>
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  <div className="flex gap-1 justify-end">
                    <button
                      onClick={() => startEdit(r)}
                      className="p-2 rounded-lg hover:bg-bone-50 text-ink-soft"
                      data-testid={`buyer-edit-${r.name}`}
                    >
                      <Pencil size={14} strokeWidth={1.75} />
                    </button>
                    <button
                      onClick={() => remove(r)}
                      className="p-2 rounded-lg hover:bg-terracotta-100 text-terracotta-700"
                      data-testid={`buyer-delete-${r.name}`}
                    >
                      <Trash2 size={14} strokeWidth={1.75} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg rounded-2xl" data-testid="buyer-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {editing ? "Edit Buyer" : "New Buyer"}
            </DialogTitle>
            <DialogDescription>
              Buyers are assigned to a single employee. When you record a bill, only
              that employee&apos;s buyers appear.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label>Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                className="hygenix-input"
                data-testid="buyer-name"
              />
            </div>
            <div className="col-span-2">
              <Label>Assigned employee *</Label>
              <Select
                value={form.employee_id}
                onValueChange={(v) => setForm({ ...form, employee_id: v })}
              >
                <SelectTrigger className="hygenix-input" data-testid="buyer-employee-select">
                  <SelectValue placeholder="Select an employee" />
                </SelectTrigger>
                <SelectContent>
                  {employees.map((e) => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Contact</Label>
              <Input
                value={form.contact}
                onChange={(e) => setForm({ ...form, contact: e.target.value })}
                className="hygenix-input"
                data-testid="buyer-contact"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="hygenix-input"
                data-testid="buyer-email"
              />
            </div>
            <div className="col-span-2">
              <Label>Address</Label>
              <Textarea
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                rows={2}
                className="hygenix-input"
                data-testid="buyer-address"
              />
            </div>
            <div className="col-span-2 pt-2">
              <Button
                type="submit"
                className="w-full btn-primary py-5"
                data-testid="buyer-submit"
              >
                {editing ? "Save changes" : "Create buyer"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
